import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { goBack, CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { darken } from 'polished';

import AppState from '@webapp/store/state';

import SimpleArtist from '@common/types/artist/simple-artist';

import AddIcon from '@components/basics/icons/add';
import BackIcon from '@components/basics/icons/back';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import ArtistSelectorSwiper from '@components/actions/artist-selector-swiper';

import ArtistSelectorItem from './artist-item';
import { isBusinessLabel } from '@webapp/store/subscription/selectors';

export interface ArtistSelectorPageProps {
  className?: string;
  artists?: SimpleArtist[];
  isMobile?: boolean;
  showAddArtist: boolean;
  goBack: () => CallHistoryMethodAction;
  createNewArtist: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artists: state.artist.myArtists,
  isMobile: state.ui.mobile,
  showAddArtist:
    state.artist.myArtists.length < 2 ||
    isBusinessLabel(
      state.recordLabels.active?.id
        ? state.subscription.label[state.recordLabels.active.id]
        : undefined
    ),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
  createNewArtist: () => dispatch(push('/artist/new')),
});

export const ArtistSelectorPage: React.FC<ArtistSelectorPageProps> = ({
  className,
  artists,
  isMobile,
  showAddArtist,
  goBack,
  createNewArtist,
}) => {
  const handleClick = () => {
    return;
  };

  return (
    <MotionOpacity className={className}>
      <Wrapper>
        <BackIcon className="back-icon" onClick={() => goBack()} />
        <Typography
          className="title"
          type="subheader"
          align="center"
          customFont="bold"
        >
          <Translate id="ARTIST_SELECTOR.TITLE" />
        </Typography>
        {artists && (
          <Content>
            {artists.length > 3 && !isMobile ? (
              <ArtistSelectorSwiper
                className={className}
                id="ArtistSelector"
                slidesLength={artists?.length}
              >
                {artists.map((artist) => (
                  <ArtistSelectorItem
                    onClick={handleClick}
                    artist={artist}
                    key={artist.id}
                  />
                ))}
              </ArtistSelectorSwiper>
            ) : (
              artists.map((artist) => (
                <ArtistSelectorItem
                  onClick={handleClick}
                  artist={artist}
                  key={artist.id}
                />
              ))
            )}
          </Content>
        )}
        {!showAddArtist ? (
          <NewArtistWrapper onClick={createNewArtist}>
            <IconWrapper>
              <AddIcon />
            </IconWrapper>
            <Info>
              <Title customFont="bold">
                <Translate id="ARTIST_SELECTOR.NEW_ARTIST.TITLE" />
              </Title>
              <Text>
                <Translate id="ARTIST_SELECTOR.NEW_ARTIST.TEXT" />
              </Text>
            </Info>
          </NewArtistWrapper>
        ) : null}
      </Wrapper>
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ArtistSelectorPage);

const Wrapper = styled.section`
  width: 100%;
  padding: 32px 24px;
  display: grid;
  grid-auto-rows: min-content;
  grid-gap: 30px;
  position: relative;
  min-height: calc(100vh - 101px);

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    grid-template-columns: repeat(24, 1fr);
    padding: 80px 24px;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    padding: 80px 0;
  }

  .back-icon {
    grid-row: 1;
    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      grid-row: auto;
      position: absolute;
      left: 24px;
      top: 80px;
    }
  }

  .title {
    width: 100%;
    grid-row: 2;
    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      grid-column: 9 / 19;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      grid-column: 10 / 18;
    }
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
  grid-row: 3;
  max-width: 100%;
  overflow: hidden;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    grid-column: 7 / 21;
    flex-direction: row;
  }
`;

const NewArtistWrapper = styled.div`
  grid-row: 4;
  margin-top: 32px;
  cursor: pointer;
  padding: 32px;
  height: max-content;
  border-radius: 10px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  background-color: ${({ theme }) => theme.colors.midGray};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.colors.midGray)};
  }

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-top: 50px;
    grid-column: 9 / 19;
  }
`;

const IconWrapper = styled.div`
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const Info = styled.div``;

const Title = styled(Typography)`
  font-weight: 700;
  margin-bottom: 5px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Text = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.span};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.tertiary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
